import { Box, FormControl, IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import CaseInfoBox from "./CaseInfo";
import CaseActions from "./Actions";
import CaseIntakesOverview from "./Intakes";
import CaseExports from "./Exports";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { selectCase, selectCaseClaimantName } from "redux/slices/case/";
import { selectClientLensAssignees, selectClientLensCaseHomeGroups } from "redux/slices/clientLens";
import { selectCollectEnabled, selectEnableFirmFeatureSet, selectIsOwnerOrSupport } from "redux/slices/session";
import CopyToClipboard from "components/CopyToClipboard";
import { createPortal } from "react-dom";
import { AppRoutes } from "AppRoutes";
import CaseAutocomplete from "components/CaseAutocomplete";
import { PortalContext } from "contexts/PortalContext";
import { useContext, useState } from "react";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { CaseDisplayGroups } from "./CaseDisplayGroups";
import People from "@mui/icons-material/People";
import EditIcon from "@mui/icons-material/Edit";
import CollaboratorFirmPickerDialog from "components/CollaboratorFirmPickerDialog";
import { useGroupLocations } from "routes/lens-editor/custom-properties/useGroupLocations";
import PlaintiffFirmPickerDialog from "components/PlaintiffFirmPickerDialog";

export default function CaseHome() {
  const navigate = useNavigate();
  const { lensId, caseId } = useParamNumbers(["lensId", "caseId"]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const lensCase = useAppSelector(selectCase);
  const caseInfo = lensCase.info;
  const clientLensAssignees = useAppSelector(selectClientLensAssignees);
  const showFirm = useAppSelector(selectEnableFirmFeatureSet);
  const claimantName = useAppSelector(selectCaseClaimantName);
  const caseHomeDisplayGroups = useAppSelector(selectClientLensCaseHomeGroups);
  const groupLocations = useGroupLocations(caseHomeDisplayGroups);
  const [showCollaboratorFirmPicker, setShowCollaboratorFirmPicker] = useState(false);
  const [showPlaintiffFirmPicker, setShowPlaintififFirmPicker] = useState(false);
  const { portalRefElement } = useContext(PortalContext);
  const collectEnabled = useAppSelector(selectCollectEnabled);
  const isOwnerOrSupport = useAppSelector(selectIsOwnerOrSupport);

  if (lensId === undefined || caseId === undefined) {
    navigate("../");
    return null;
  }

  const navigateToCase = (caseId: number) => {
    navigate(AppRoutes.caseDetailBuilder(lensId, caseId));
  };

  return (
    <>
      {portalRefElement &&
        createPortal(
          <Box sx={{ display: "flex" }}>
            {!isMobile && (
              <FormControl sx={{ width: "300px", ml: 2 }}>
                <CaseAutocomplete
                  label="Search for a case"
                  selected={undefined}
                  onChange={(selectedCase) => {
                    if (selectedCase) {
                      navigateToCase(selectedCase.caseId);
                    }
                  }}
                  lensId={lensId}
                />
              </FormControl>
            )}
          </Box>,
          portalRefElement
        )}
      <div className="flex-1 min-h-0 h-full flex flex-col gap-4 w-full min-w-0 pt-4">
        <div className="flex *:items-center divide-x divide-gray-100">
          <div className="flex pr-4">
            <p className="font-bold text-2xl">{claimantName}</p>
            <CopyToClipboard sx={{ p: 0, fontSize: 16 }} text={claimantName} />
          </div>
          <div className="flex px-4">
            <p className="font-bold text-gray-300">{caseInfo.clientCaseId}</p>
            <CopyToClipboard sx={{ p: 0, fontSize: 16 }} text={caseInfo.clientCaseId} />
          </div>
          {showFirm && (
            <div className="flex px-4">
              {lensCase.plaintiffFirm?.name ? (
                <p className="font-bold text-gray-300">{lensCase.plaintiffFirm.name}</p>
              ) : (
                <p className="text-gray-300">No Firm</p>
              )}
              {isOwnerOrSupport && (
                <div>
                  <Tooltip title="Edit Primary Firm">
                    <IconButton
                      className="pl-2"
                      onClick={() => {
                        setShowPlaintififFirmPicker(true);
                      }}>
                      <EditIcon fontSize="inherit" className="text-base" />
                    </IconButton>
                  </Tooltip>
                  <PlaintiffFirmPickerDialog
                    open={showPlaintiffFirmPicker}
                    setOpen={setShowPlaintififFirmPicker}
                    lensId={lensId}
                  />
                </div>
              )}
              {collectEnabled && (
                <div>
                  <Tooltip title="Edit Collaborator Firms">
                    <IconButton
                      className="p-0"
                      onClick={() => {
                        setShowCollaboratorFirmPicker(true);
                      }}>
                      <People fontSize="inherit" className="text-base" />
                    </IconButton>
                  </Tooltip>
                  {showCollaboratorFirmPicker && (
                    <CollaboratorFirmPickerDialog
                      open={showCollaboratorFirmPicker}
                      setOpen={setShowCollaboratorFirmPicker}
                      lensId={lensId}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 2,
          }}>
          <CaseInfoBox
            sx={{
              flex: 2,
            }}
            ccase={lensCase}
            assignees={clientLensAssignees}
            initialEditMode={false}
          />
          <CaseActions
            sx={{
              flex: 1,
            }}
            lensCase={lensCase}
          />
        </Box>
        <div>
          <CaseDisplayGroups
            sx={{ maxHeight: 334, overflowY: "auto" }}
            groups={groupLocations.caseHome.map((gl) => gl.group)}
          />
        </div>
        {/* Documents & Exports */}
        <Box
          sx={{
            minHeight: 0,
            height: "100%",
            display: "flex",
            flexDirection: "row",
            gap: 2,
            width: "100%",
          }}>
          <CaseIntakesOverview lensId={lensId} caseId={caseId} lensCase={lensCase} />
          <CaseExports
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              height: "100%",
              width: 400,
            }}
            caseId={caseId}
          />
        </Box>
      </div>
    </>
  );
}
